"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, Calendar as CalendarIcon, Clock, Users, Car, Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-reveal">
        <div className="size-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-display font-semibold text-foreground mb-2">Request Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="btn-secondary">
          Book Another Transfer
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {}
      <FormField control={control} name="pickup" rules={{
      required: "Pick-up location is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Pick-up Location</FormLabel>
            <FormControl>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input {...field} className="pl-10 bg-[rgba(255,255,255,0.9)] border-gray-200 shadow-inner h-12" placeholder="Airport, Hotel, or Address" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="dropoff" rules={{
      required: "Drop-off location is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Drop-off Location</FormLabel>
            <FormControl>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input {...field} className="pl-10 bg-[rgba(255,255,255,0.9)] border-gray-200 shadow-inner h-12" placeholder="Destination Address" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="date" rules={{
      required: "Date is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Pick-up Date</FormLabel>
            <FormControl>
              <div className="relative">
                <SafeHydrate><CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" /></SafeHydrate>
                <Input type="date" {...field} className="pl-10 bg-[rgba(255,255,255,0.9)] border-gray-200 shadow-inner h-12" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="time" rules={{
      required: "Time is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Pick-up Time</FormLabel>
            <FormControl>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input type="time" {...field} className="pl-10 bg-[rgba(255,255,255,0.9)] border-gray-200 shadow-inner h-12" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="passengers" rules={{
      required: "Number of passengers is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Passengers</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground z-10" />
                  <SelectTrigger className="pl-10 bg-[rgba(255,255,255,0.9)] border-gray-200 shadow-inner h-12">
                    <SelectValue placeholder="Select passengers" />
                  </SelectTrigger>
                </div>
              </FormControl>
              <SelectContent>
                <SelectItem value="1">1 Passenger</SelectItem>
                <SelectItem value="2">2 Passengers</SelectItem>
                <SelectItem value="3">3 Passengers</SelectItem>
                <SelectItem value="4">4 Passengers</SelectItem>
                <SelectItem value="5">5 Passengers</SelectItem>
                <SelectItem value="6">6+ Passengers</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="vehicle" rules={{
      required: "Vehicle class is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Vehicle Class</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <div className="relative">
                  <Car className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground z-10" />
                  <SelectTrigger className="pl-10 bg-[rgba(255,255,255,0.9)] border-gray-200 shadow-inner h-12">
                    <SelectValue placeholder="Select vehicle class" />
                  </SelectTrigger>
                </div>
              </FormControl>
              <SelectContent>
                <SelectItem value="sedan">Luxury Sedan</SelectItem>
                <SelectItem value="suv">Premium SUV</SelectItem>
                <SelectItem value="sprinter">Executive Sprinter</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="md:col-span-2 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      {}
      <div className="md:col-span-2 mt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full btn-primary h-14 text-base tracking-wide">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing Request...
            </> : 'Reserve Now'}
        </Button>
      </div>
    </Form>;
}
export default function BookYourTransfer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="book-your-transfer" className="relative bg-background section-padding overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/generic_luxury_bg.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/generic_luxury_bg.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/generic_luxury_bg.png-wvc-sizes" alt="Luxury background" className="w-full h-full object-cover opacity-5" loading="lazy" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-4xl mx-auto bg-[rgba(255,255,255,0.6)] backdrop-blur-[8px] border border-white/40 shadow-strong rounded-2xl p-6 md:p-12 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              Reserve Your Private Chauffeur
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Experience seamless luxury travel. Book your transfer in advance and enjoy a premium, stress-free journey.
            </p>
          </div>

          <FormProvider formId="booking_form_home" sectionName="Book Your Transfer" defaultValues={{
          pickup: "",
          dropoff: "",
          date: "",
          time: "",
          passengers: "",
          vehicle: ""
        }} submitText="Reserve Now" successMessage="Thank you for your reservation request. Our team will contact you shortly to confirm your booking details.">
            <BookingForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}